import { Observable, concat, from, of } from 'rxjs';
import { Action } from '@reduxjs/toolkit';
import { ofType } from 'redux-observable';
import { filter, mergeMap, catchError } from 'rxjs/operators';

import { authActions } from './authSlice';
import { appAction } from '../../../app/redux/appSlice';
import { authenticateUser } from '../../../services/api/userApi';
import { organizationActions } from '../../dashboard/redux/organizationSlice';
import { setBearerToken } from '../../../services/api/baseApi';
import { navigationActions } from '../../../app/redux/navigationSlice';
import storage from '../../../services/storage/localStorage';
import authConstants from '../../../constants/storage';
import { notificationActions } from '../../../app/redux/notification.slice';
import { getUserRole } from '../../../services/api/userRoleApi';
import { roleTypes } from '../../../constants/userRole';
import { HOME } from '../../dashboard/routes';
import { RootState } from '../../../app/redux/rootReducer';

const login = (action$: Observable<Action>): Observable<Action> =>
  // eslint-disable-next-line
  action$.pipe(
    ofType(authActions.login),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    filter(authActions.login.match),
    mergeMap((action) =>
      concat(
        of(appAction.setLoading(true)),
        from(authenticateUser(action.payload)).pipe(
          mergeMap((response) =>
            of(
              authActions.authenticateUserSuccess({
                ...response,
              }),
              navigationActions.setNavigationDetails({
                pathname: HOME,
                queryParams: {},
              })
            )
          ),
          catchError((error: Error, source) =>
            of(
              appAction.loadingSuccess(),
              notificationActions.setToast({
                severity: 'danger',
                message: error.message,
                title: `Error: Invalid User Credentials`,
              })
            )
          )
        )
      )
    )
  );

const fetchUserRole = (action$: Observable<Action>): Observable<Action> =>
  // eslint-disable-next-line
  action$.pipe(
    ofType(authActions.fetchUserRole),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    filter(authActions.fetchUserRole.match),
    mergeMap((action) =>
      from(getUserRole(action.payload)).pipe(
        mergeMap((response) => {
          if (response.roleName != roleTypes.SUPER_ADMIN) {
            return concat(
              of(authActions.setUserRole(response)),
              of(organizationActions.fetchOverview()),
              of(organizationActions.fetchMachineList())
            );
          }
          return concat(
            of(authActions.setUserRole(response)),
            of(appAction.fetchCompanyTypeList())
          );
        })
      )
    ),
    catchError((error: Error) =>
      of(
        notificationActions.setToast({
          severity: 'danger',
          message: error.message,
          title: `Error: Couldn't Fetch User Role`,
        })
      )
    )
  );

const loginSuccess = (action$: Observable<Action>): Observable<Action> =>
  // eslint-disable-next-line
  action$.pipe(
    ofType(authActions.authenticateUserSuccess),
    // eslint-disable-next-line @typescript-eslint/unbound-method
    filter(authActions.authenticateUserSuccess.match),
    mergeMap((action) => {
      const { selectedCategoryId, ...user } = { ...action.payload };
      setBearerToken(user.token);
      storage.set(authConstants.AUTH_RESPONSE, user);

      if (selectedCategoryId) {
        return of(
          appAction.fetchUserRoleList(),
          authActions.fetchUserRole(user.roleId),
          organizationActions.fetchMachineTypeList(),
          organizationActions.setSelectedCategory(selectedCategoryId),
          organizationActions.fetchWorkshopList(selectedCategoryId),
          organizationActions.fetchActivityTypeList(),
          appAction.fetchUserList(),
          organizationActions.fetCategoryList(),
          appAction.setLoading(false)
        );
      }

      return of(
        appAction.fetchUserRoleList(),
        authActions.fetchUserRole(user.roleId),
        organizationActions.fetchMachineTypeList(),
        organizationActions.fetchActivityTypeList(),
        appAction.fetchUserList(),
        organizationActions.fetCategoryList(),
        appAction.setLoading(false)
      );
    })
  );

export const authEpic = [login, loginSuccess, fetchUserRole];
